import { useState, useEffect } from "react"
import { Helmet } from "react-helmet-async"
import { BrandingConfig } from "@/components/branding/BrandingConfig"
import { LogoUpload } from "@/components/branding/LogoUpload"
import { PreviewPanel } from "@/components/branding/PreviewPanel"
import { Navigation } from "@/components/Navigation"
import { ToastContainer } from "@/components/ui/toast"
import { useToast } from "@/hooks/use-toast"
import {
  getBrandingConfig,
  updateBrandingConfig,
  uploadLogo,
  deleteLogo,
  resetBranding,
  getLogoUrl,
  updateColorScheme,
  checkLogoExists
} from "@/lib/brandingApi"

export default function Branding() {
  const { toasts, toast, removeToast } = useToast()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [logoUploading, setLogoUploading] = useState(false)

  const [brandingConfig, setBrandingConfig] = useState({
    companyName: "",
    companyTagline: "",
    primaryEmail: "",
    phone: "",
    primaryColor: "#1e40af",
    secondaryColor: "#3b82f6",
    accentColor: "#60a5fa"
  })

  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string>("")
  const [existingLogoFilename, setExistingLogoFilename] = useState<string>("")

  // Load existing branding config on mount
  useEffect(() => {
    loadBrandingConfig()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadBrandingConfig = async () => {
    setLoading(true)
    try {
      const config = await getBrandingConfig()
      if (config) {
        setBrandingConfig({
          companyName: config.contact_info?.company_name || "",
          companyTagline: config.messaging?.company_tagline || "",
          primaryEmail: config.contact_info?.primary_email || "",
          phone: config.contact_info?.phone || "",
          primaryColor: config.color_scheme?.primary_color || "#1e40af",
          secondaryColor: config.color_scheme?.secondary_color || "#3b82f6",
          accentColor: config.color_scheme?.success_color || "#60a5fa"
        })

        // Set existing logo if available and verify it exists, or clear if not
        if (config.branding_assets?.logo_filename) {
          setExistingLogoFilename(config.branding_assets.logo_filename)

          // Check if the logo file actually exists on the server
          const logoExists = await checkLogoExists(config.branding_assets.logo_filename)
          if (logoExists) {
            const logoUrl = getLogoUrl(config.branding_assets.logo_filename)
            if (logoUrl) {
              setLogoPreviewUrl(logoUrl)
            }
          } else {
            // Logo filename in DB but file doesn't exist - clear the state
            console.warn(`Logo file ${config.branding_assets.logo_filename} not found on server`)
            setExistingLogoFilename("")
            setLogoPreviewUrl("")
            // Don't show error toast since logo is optional
          }
        } else {
          // Clear logo state when no logo exists
          setExistingLogoFilename("")
          setLogoPreviewUrl("")
        }
        // Logo files are uploaded immediately, no file state to clear
      }
    } catch (error) {
      console.error('Failed to load branding config:', error)
      toast({
        title: "Error",
        description: "Failed to load branding configuration",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const handleLogoChange = async (file: File | null) => {
    if (file) {
      setLogoUploading(true)

      // Show immediate preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setLogoPreviewUrl(e.target?.result as string)
      }
      reader.readAsDataURL(file)

      // Upload immediately
      try {
        const logoResult = await uploadLogo(file, 'logo')
        if (!logoResult.success) {
          throw new Error(logoResult.message || 'Failed to upload logo')
        }

        if (logoResult.filename) {
          setExistingLogoFilename(logoResult.filename)

          // Verify the uploaded file is accessible
          const logoExists = await checkLogoExists(logoResult.filename)
          if (logoExists) {
            const newLogoUrl = getLogoUrl(logoResult.filename)
            if (newLogoUrl) {
              setLogoPreviewUrl(newLogoUrl)
            }

            toast({
              title: "Success",
              description: "Logo uploaded successfully",
              variant: "success"
            })
          } else {
            throw new Error('Logo uploaded but is not accessible from server')
          }
        }

        // File uploaded successfully, no local file state to clear
      } catch (error) {
        console.error('Failed to upload logo:', error)
        toast({
          title: "Upload Failed",
          description: error instanceof Error ? error.message : "Failed to upload logo",
          variant: "destructive"
        })
        // Reset preview on failure
        setLogoPreviewUrl("")
      } finally {
        setLogoUploading(false)
      }
    } else if (!existingLogoFilename) {
      setLogoPreviewUrl("")
    }
  }

  const handleSaveChanges = async () => {
    setSaving(true)
    try {
      // Update branding configuration (logo is uploaded immediately on selection)
      const updateResult = await updateBrandingConfig({
        contact_info: {
          company_name: brandingConfig.companyName || "",
          primary_email: brandingConfig.primaryEmail || "",
          phone: brandingConfig.phone || ""
        },
        messaging: {
          company_tagline: brandingConfig.companyTagline || ""
        }
      })

      if (!updateResult.success) {
        throw new Error(updateResult.message || 'Failed to update branding')
      }

      // Update color scheme
      const colorResult = await updateColorScheme({
        primary_color: brandingConfig.primaryColor,
        secondary_color: brandingConfig.secondaryColor,
        success_color: brandingConfig.accentColor
      })

      if (!colorResult.success) {
        throw new Error(colorResult.message || 'Failed to update colors')
      }

      toast({
        title: "Success",
        description: "Branding configuration saved successfully",
        variant: "success"
      })

      // Clear the file input after successful save
      // Logo delete failed, no file state to reset

      // Reload config to ensure preview is in sync
      await loadBrandingConfig()
    } catch (error) {
      console.error('Failed to save branding:', error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save branding configuration",
        variant: "destructive"
      })
    } finally {
      setSaving(false)
    }
  }

  const performReset = async () => {
    setSaving(true)
    try {
      const result = await resetBranding()
      if (!result.success) {
        throw new Error(result.message || 'Failed to reset branding')
      }

      toast({
        title: "Success",
        description: result.message || "Branding reset to defaults",
        variant: "success"
      })

      // Reload the configuration
      await loadBrandingConfig()
      setLogoPreviewUrl("")
      setExistingLogoFilename("")
    } catch (error) {
      console.error('Failed to reset branding:', error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to reset branding",
        variant: "destructive"
      })
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    toast({
      title: "Reset Branding",
      description: "This will reset all branding settings to their defaults. This action cannot be undone.",
      variant: "confirmation",
      actions: {
        cancel: {
          label: "Cancel",
          onClick: () => {} // No action needed, toast will be dismissed
        },
        confirm: {
          label: "Reset",
          onClick: performReset
        }
      }
    })
  }

  const performDeleteLogo = async () => {
    try {
      const result = await deleteLogo('logo')
      if (!result.success) {
        throw new Error(result.message || 'Failed to delete logo')
      }

      toast({
        title: "Success",
        description: result.message || "Logo deleted successfully",
        variant: "success"
      })

      setLogoPreviewUrl("")
      setExistingLogoFilename("")
    } catch (error) {
      console.error('Failed to delete logo:', error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete logo",
        variant: "destructive"
      })
    }
  }

  const handleDeleteLogo = () => {
    toast({
      title: "Delete Logo",
      description: "Are you sure you want to delete the company logo? This action cannot be undone.",
      variant: "confirmation",
      actions: {
        cancel: {
          label: "Cancel",
          onClick: () => {} // No action needed, toast will be dismissed
        },
        confirm: {
          label: "Delete",
          onClick: performDeleteLogo
        }
      }
    })
  }

  if (loading) {
    return (
      <div className="flex min-h-screen w-full flex-col">
        <Navigation />
        <main className="flex flex-1 flex-col gap-4 px-4 pb-4 md:gap-8 md:px-8 md:pb-8 pt-20">
          <div className="mx-auto grid w-full max-w-6xl gap-6">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-muted-foreground">Loading branding configuration...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen w-full flex-col">
      <Helmet>
        <title>Branding | Fusegu Security</title>
        <meta name="description" content="Customize your security report branding and appearance." />
      </Helmet>
      <Navigation />
      <ToastContainer toasts={toasts} onDismiss={removeToast} />

      <main className="flex flex-1 flex-col gap-4 px-4 pb-4 md:gap-8 md:px-8 md:pb-8 pt-20">
        <div className="mx-auto grid w-full max-w-6xl gap-6">
          <div>
            <h1 className="text-3xl font-bold">Brand Management</h1>
            <p className="text-muted-foreground mt-2">
              Customize your organization's branding for security reports and documentation
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-6">
              <BrandingConfig
                config={brandingConfig}
                onConfigChange={setBrandingConfig}
                onSave={handleSaveChanges}
                onReset={handleReset}
                saving={saving}
              />

              <LogoUpload
                logoUrl={logoPreviewUrl}
                onLogoChange={handleLogoChange}
                onDeleteLogo={existingLogoFilename ? handleDeleteLogo : undefined}
                hasExistingLogo={!!existingLogoFilename}
                isUploading={logoUploading}
              />
            </div>

            <div className="space-y-6">
              <PreviewPanel
                config={brandingConfig}
                logoUrl={logoPreviewUrl}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}